

'use client';

import React, { useState, useEffect } from "react";
import Image from "next/image";
import Link from "next/link";

interface EstatisticaItem {
    id: string;
    number: string;
    label: string;
    icon: string | React.ReactNode;
}

interface FPEFEmNumerosProps {
    title?: string;
    tag?: string;
    description?: string;
    buttonLabel?: string;
    buttonLink?: string;
    stats?: EstatisticaItem[];
    onStatClick?: (statId: string) => void;
    backgroundImage?: string;
}

const defaultStats: EstatisticaItem[] = [
    {
        id: "beneficiarios",
        number: "14.120",
        label: "Beneficiários",
        icon: "/a.png",
    },
    {
        id: "empreendedores",
        number: "316",
        label: "Empreendedores\nFinanciados",
        icon: "/a.png",
    },
    {
        id: "financiamento",
        number: "115 860 078 M",
        label: "Em\nFinanciamento",
        icon: "/a.png",
    },
    {
        id: "financiamento",
        number: "115 860 078 M",
        label: "Em\nFinanciamento",
        icon: "/a.png",
    },
    {
        id: "financiamento",
        number: "115 860 078 M",
        label: "Em\nFinanciamento",
        icon: "/a.png",
    },
    {
        id: "financiamento",
        number: "115 860 078 M",
        label: "Em\nFinanciamento",
        icon: "/a.png",
    },
];

export default function FPEFEmNumeros({
    title = "O que dizem os nossos dados",
    tag = "FPEF em números",
    description = "Milhares de jovens já foram beneficiados pelos nossos programas de fomento ao emprego e à formação profissional",
    buttonLabel = "Ver Relatório",
    buttonLink = "/relatorio",
    stats = defaultStats,
    onStatClick,
    backgroundImage = "/news.png",
}: FPEFEmNumerosProps) {
    const [currentSlide, setCurrentSlide] = useState(0);
    const [isAutoPlay, setIsAutoPlay] = useState(true);
    const [touchStart, setTouchStart] = useState(0);
    const [touchEnd, setTouchEnd] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const [itemsPerView, setItemsPerView] = useState(3);
    const totalSlides = Math.ceil(stats.length / itemsPerView);

    // Responsive items per view
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth < 768) {
                setItemsPerView(1); // mobile
            } else if (window.innerWidth < 1024) {
                setItemsPerView(2); // tablet
            } else {
                setItemsPerView(3); // desktop
            }
        };
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Auto-play carousel
    useEffect(() => {
        if (!isAutoPlay) return;

        const timer = setInterval(() => {
            setCurrentSlide((prev) => (prev + 1) % totalSlides);
        }, 5000);

        return () => clearInterval(timer);
    }, [isAutoPlay, totalSlides]);

    // Swipe handlers
    const handleTouchStart = (e: React.TouchEvent) => {
        setTouchStart(e.targetTouches[0].clientX);
    };

    const handleTouchMove = (e: React.TouchEvent) => {
        setTouchEnd(e.targetTouches[0].clientX);
    };

    const handleTouchEnd = () => {
        if (!touchStart || !touchEnd) return;

        const distance = touchStart - touchEnd;
        const isLeftSwipe = distance > 50;
        const isRightSwipe = distance < -50;

        if (isLeftSwipe && currentSlide < totalSlides - 1) {
            setCurrentSlide((prev) => prev + 1);
            setIsAutoPlay(false);
        }
        if (isRightSwipe && currentSlide > 0) {
            setCurrentSlide((prev) => prev - 1);
            setIsAutoPlay(false);
        }

        setTouchStart(0);
        setTouchEnd(0);
    };

    // Mouse drag handlers for desktop
    const handleMouseDown = (e: React.MouseEvent) => {
        setIsDragging(true);
        setTouchStart(e.clientX);
        e.preventDefault();
    };

    const handleMouseMove = (e: React.MouseEvent) => {
        if (!isDragging) return;
        setTouchEnd(e.clientX);
    };

    const handleMouseUp = () => {
        if (!isDragging) return;
        setIsDragging(false);

        if (!touchStart || !touchEnd) return;

        const distance = touchStart - touchEnd;
        const isLeftSwipe = distance > 50;
        const isRightSwipe = distance < -50;

        if (isLeftSwipe && currentSlide < totalSlides - 1) {
            setCurrentSlide((prev) => prev + 1);
            setIsAutoPlay(false);
        }
        if (isRightSwipe && currentSlide > 0) {
            setCurrentSlide((prev) => prev - 1);
            setIsAutoPlay(false);
        }

        setTouchStart(0);
        setTouchEnd(0);
    };

    const handleMouseLeave = () => {
        if (isDragging) {
            setIsDragging(false);
            setTouchStart(0);
            setTouchEnd(0);
        }
    };

    const handleStatClick = (statId: string) => {
        if (onStatClick) {
            onStatClick(statId);
        }
    };
    /* 
        const handlePrevSlide = () => {
            setIsAutoPlay(false);
            setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
        };
    
        const handleNextSlide = () => {
            setIsAutoPlay(false);
            setCurrentSlide((prev) => (prev + 1) % totalSlides);
        }; */

    return (
        <section className="w-full relative overflow-hidden min-h-[500px] sm:min-h-[600px] md:min-h-[700px] lg:min-h-[800px]">
            {/* Background Image */}
            <div
                className="absolute inset-0 bg-cover bg-center scale-110"
                style={{ backgroundImage: `url('${backgroundImage}')` }}
                aria-hidden="true"
            />

            {/* SVG Gradient Overlay - Mobile */}
            <svg
                className="md:hidden absolute bottom-0 left-0 right-0 w-full h-full pointer-events-none"
                viewBox="0 0 1920 490"
                xmlns="http://www.w3.org/2000/svg"
                preserveAspectRatio="none"
                aria-hidden="true"
            >
                <defs>
                    <linearGradient id="paint0_linear_mobile" x1="305.421" y1="138.814" x2="1743.34" y2="900.336" gradientUnits="userSpaceOnUse">
                        <stop offset="0.0257575" stopColor="#EF4130" stopOpacity="0.8" />
                        <stop offset="0.254366" stopColor="#F15A29" stopOpacity="0.7" />
                        <stop offset="0.367337" stopColor="#F6881F" stopOpacity="0.5" />
                        <stop offset="0.586538" stopColor="#F68D1E" stopOpacity="0.4" />
                        <stop offset="0.764423" stopColor="#F7941D" stopOpacity="0.5" />
                        <stop offset="1" stopColor="#F15A29" stopOpacity="0.7" />
                    </linearGradient>
                </defs>
                <path
                    d="M0 440 V490 H199.477 V489.782 L0 440 Z"
                    fill="white"
                />
                <path
                    d="M1920 0V489.955H199.661V490.002L199.622 489.955H199.477V489.782L0 440V0.0488281L199.477 0.046875V0H1920Z"
                    fill="url(#paint0_linear_mobile)"
                />
            </svg>

            {/* SVG Gradient Overlay - Tablet */}
            <svg
                className="hidden md:block lg:hidden absolute bottom-0 left-0 right-0 w-full h-full pointer-events-none"
                viewBox="0 0 1920 490"
                xmlns="http://www.w3.org/2000/svg"
                preserveAspectRatio="none"
                aria-hidden="true"
            >
                <defs>
                    <linearGradient id="paint0_linear_tablet" x1="305.421" y1="138.814" x2="1743.34" y2="900.336" gradientUnits="userSpaceOnUse">
                        <stop offset="0.0257575" stopColor="#EF4130" stopOpacity="0.8" />
                        <stop offset="0.254366" stopColor="#F15A29" stopOpacity="0.7" />
                        <stop offset="0.367337" stopColor="#F6881F" stopOpacity="0.5" />
                        <stop offset="0.586538" stopColor="#F68D1E" stopOpacity="0.4" />
                        <stop offset="0.764423" stopColor="#F7941D" stopOpacity="0.5" />
                        <stop offset="1" stopColor="#F15A29" stopOpacity="0.7" />
                    </linearGradient>
                </defs>
                <path
                    d="M0 380 V490 H199.477 V489.782 L0 380 Z"
                    fill="white"
                />
                <path
                    d="M1920 0V489.955H199.661V490.002L199.622 489.955H199.477V489.782L0 380V0.0488281L199.477 0.046875V0H1920Z"
                    fill="url(#paint0_linear_tablet)"
                />
            </svg>

            {/* SVG Gradient Overlay - Desktop */}
            <svg
                className="hidden lg:block absolute bottom-0 left-0 right-0 w-full h-full pointer-events-none"
                viewBox="0 0 1920 490"
                xmlns="http://www.w3.org/2000/svg"
                preserveAspectRatio="none"
                aria-hidden="true"
            >
                <defs>
                    <linearGradient id="paint0_linear_desktop" x1="305.421" y1="138.814" x2="1743.34" y2="900.336" gradientUnits="userSpaceOnUse">
                        <stop offset="0.0257575" stopColor="#EF4130" stopOpacity="0.8" />
                        <stop offset="0.254366" stopColor="#F15A29" stopOpacity="0.7" />
                        <stop offset="0.367337" stopColor="#F6881F" stopOpacity="0.5" />
                        <stop offset="0.586538" stopColor="#F68D1E" stopOpacity="0.4" />
                        <stop offset="0.764423" stopColor="#F7941D" stopOpacity="0.5" />
                        <stop offset="1" stopColor="#F15A29" stopOpacity="0.7" />
                    </linearGradient>
                </defs>
                <path
                    d="M0 378 V490 H199.477 V489.782 L0 378 Z"
                    fill="white"
                />
                <path
                    d="M1920 0V489.955H199.661V490.002L199.622 489.955H199.477V489.782L0 378V0.0488281L199.477 0.046875V0H1920Z"
                    fill="url(#paint0_linear_desktop)"
                />
            </svg>

            {/* Content Container */}
            <div className="relative z-10">
                <div className="container pt-12 sm:pt-16 md:pt-20 lg:pt-24 pb-8 sm:pb-12 md:pb-6 px-4 sm:px-6 md:px-8 lg:px-12 xl:px-16 mx-auto">
                    {/* Main Grid - Left Text, Right Carousel */}
                    <div className="flex flex-col lg:flex-row lg:gap-12 xl:gap-16 items-center lg:items-center">
                        {/* Left Side - Text Content */}
                        <div className="flex flex-col justify-start gap-3 sm:gap-4 lg:max-w-[300px] xl:max-w-[320px] mb-8 lg:mb-0">
                            {/* Subtitle */}
                            <p className="text-xs sm:text-sm font-semibold font-nunito text-zinc-900 uppercase tracking-wide bg-stone-50/30 rounded-xs px-2 py-1 w-fit">
                                {tag}
                            </p>

                            {/* Title */}
                            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold uppercase font-barlow-semi-condensed text-white leading-tight max-w-xl">
                                {title}
                            </h2>

                            {/* Description */}
                            <div className="text-sm sm:text-base md:text-lg text-white font-nunito leading-relaxed font-medium" dangerouslySetInnerHTML={{ __html: description }} />

                            {/* Button */}
                            <Link href={buttonLink}>
                                <button className="w-full sm:w-auto min-w-[180px] cursor-pointer hover:opacity-80 hover:scale-105 transition-all px-5 sm:px-6 py-2.5 sm:py-3 rounded-sm border-2 border-yellow-500 justify-center items-center gap-4">
                                    <span className="text-white text-sm sm:text-base font-medium font-nunito">
                                        {buttonLabel}
                                    </span>
                                </button>
                            </Link>
                        </div>

                        {/* Right Side - Carousel */}
                        <div className="flex flex-col items-center lg:items-start gap-6 sm:gap-8 flex-1 w-full">
                            {/* Carousel Container */}
                            <div
                                className="w-full flex justify-center lg:justify-start relative cursor-grab active:cursor-grabbing"
                                onTouchStart={handleTouchStart}
                                onTouchMove={handleTouchMove}
                                onTouchEnd={handleTouchEnd}
                                onMouseDown={handleMouseDown}
                                onMouseMove={handleMouseMove}
                                onMouseUp={handleMouseUp}
                                onMouseLeave={handleMouseLeave}
                            >
                                {/* Stats Cards Carousel */}
                                <div className="overflow-hidden w-full">
                                    <div
                                        className="flex transition-transform duration-500 ease-out"
                                        style={{
                                            transform: `translateX(-${currentSlide * (100 / itemsPerView)}%)`,
                                        }}
                                    >
                                        {stats.map((stat) => (
                                            <div
                                                key={stat.id}
                                                className="w-full md:w-1/3 flex-shrink-0 flex justify-center lg:justify-c px-2 sm:px-3"
                                                onClick={() => handleStatClick(stat.id)}
                                            >
                                                <StatCard stat={stat} />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* Carousel Controls - Dots */}
                            <div className="flex justify-center lg:justify-start items-center gap-2 sm:gap-3">
                                {Array.from({ length: totalSlides }).map((_, index) => (
                                    <button
                                        key={index}
                                        onClick={() => {
                                            setIsAutoPlay(false);
                                            setCurrentSlide(index);
                                        }}
                                        className={`transition-all duration-300 h-1 rounded-full cursor-pointer ${index === currentSlide
                                            ? "w-16 sm:w-20 bg-gradient-to-r from-orange-500 via-orange-400 to-red-500"
                                            : "w-8 sm:w-10 bg-gray-300 hover:bg-gray-400"
                                            }`}
                                        aria-label={`Go to slide ${index + 1}`}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

interface StatCardProps {
    stat: EstatisticaItem;
}

function StatCard({ stat }: StatCardProps) {
    const isImageIcon = typeof stat.icon === 'string';

    return (
        <div className="flex flex-col items-center text-center cursor-pointer group transition-transform duration-300 hover:scale-105">
            {/* Icon with Geometric Background */}
            <div className="relative mb-6 sm:mb-8 md:mb-10 lg:mb-1">
                {/* Orange Geometric Shape - Responsive sizing */}
                <svg
                    className="w-32 h-24 sm:w-36 sm:h-28 md:w-40 md:h-32 lg:w-[157px] lg:h-[126px]"
                    viewBox="0 0 157 126"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path d="M0 76.9565V0H115.106L156.916 48.5217V126H40.7775L0 76.9565Z" fill="#F15A29" fillOpacity="0.4" />
                </svg>

                {/* Icon Container - Centered inside shape */}
                <div className="absolute inset-0 flex items-center justify-center">
                    {isImageIcon ? (
                        <Image
                            src={stat.icon as string}
                            alt={stat.label}
                            width={80}
                            height={80}
                            className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 lg:w-16 lg:h-16 object-contain"
                        />
                    ) : (
                        <div className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 lg:w-16 lg:h-16 flex items-center justify-center">
                            {stat.icon}
                        </div>
                    )}
                </div>
            </div>

            {/* Number */}
            <h3 className="text-3xl sm:text-3xl md:text-4xl lg:text-5xl font-bold font-barlow-semi-condensed text-gray-200 mb-1 sm:mb-2 leading-tight">
                {stat.number}
            </h3>

            {/* Label */}
            <p className="text-base sm:text-lg md:text-xl font-semibold font-barlow-condensed text-gray-200 leading-relaxed whitespace-pre-line">
                {stat.label}
            </p>
        </div>
    );
}